import { useEffect, useState } from 'react';
import {
  requestNotificationPermission,
  sendTokenToBackend,
  onForegroundMessage,
  deleteTokenFromBackend,
} from '../services/notificationService';
import { MessagePayload } from 'firebase/messaging';

interface UseNotificationsReturn {
  fcmToken: string | null;
  notificationPermission: NotificationPermission | null;
  lastNotification: MessagePayload | null;
  requestPermission: () => Promise<void>;
  clearToken: () => Promise<void>;
}

/**
 * Custom hook to manage Firebase Cloud Messaging notifications
 * @param apiBaseUrl Backend API base URL (optional)
 */
export const useNotifications = (apiBaseUrl?: string): UseNotificationsReturn => {
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission | null>(
      typeof window !== 'undefined' && 'Notification' in window
        ? Notification.permission
        : null
    );
  const [lastNotification, setLastNotification] = useState<MessagePayload | null>(null);

  // Request notification permission and get token
  const requestPermission = async () => {
    const token = await requestNotificationPermission();
    if (token) {
      setFcmToken(token);
      setNotificationPermission(Notification.permission);

      // Send token to backend
      await sendTokenToBackend(token, apiBaseUrl);
    }
  };

  // Clear token from backend (on logout)
  const clearToken = async () => {
    await deleteTokenFromBackend(apiBaseUrl);
    setFcmToken(null);
  };

  // Set up foreground message listener on mount
  useEffect(() => {
    // Set up foreground message handler
    onForegroundMessage((payload) => {
      setLastNotification(payload);
    });

    // Request permission automatically if not set
    if (notificationPermission === 'default') {
      // Optionally auto-request permission after user interaction
      // requestPermission();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    fcmToken,
    notificationPermission,
    lastNotification,
    requestPermission,
    clearToken,
  };
};
